import { useEffect, useSyncExternalStore } from 'react';
import sessionExpiredImg from '@/assets/images/session-expired.webp';
import { AppDialogLayout } from '@/components/system';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import {
    isSessionExpired,
    markSessionAuthenticated,
    subscribeToSessionExpiry,
} from '@/lib/session-expiry';

/**
 * Blocks the app when the session dies with the tab open.
 *
 * Deliberately not a `ModalProvider` modal: `openModal` cannot forbid Escape or
 * a click outside, and everything behind this dialog is already dead — every
 * request answers 401. Dismissing it would drop the person straight back into
 * the "it just loads forever" state this exists to end.
 */
export const SessionExpiredDialog = () => {
    const { isAuthenticated, login } = useAuth();
    const expired = useSyncExternalStore(subscribeToSessionExpiry, isSessionExpired);

    // The interceptor that raises the signal runs outside React, so it reads
    // whether there was a session at all from here.
    useEffect(() => {
        markSessionAuthenticated(isAuthenticated);
    }, [isAuthenticated]);

    if (!expired) return null;

    const signInAgain = () => {
        login({ returnTo: window.location.pathname + window.location.search });
    };

    return (
        <Dialog open>
            <DialogContent
                // Title and art carry the whole message — there is no Radix
                // description node to point at.
                aria-describedby={undefined}
                showCloseButton={false}
                // `dialog.jsx` is untyped, so these come through as `any`.
                onEscapeKeyDown={(event: Event) => event.preventDefault()}
                onInteractOutside={(event: Event) => event.preventDefault()}
                onPointerDownOutside={(event: Event) => event.preventDefault()}
                className="flex flex-col gap-0 overflow-hidden border-border/80 p-0 shadow-elevation-2 outline-none"
            >
                <AppDialogLayout
                    title="Sua sessão expirou"
                    footer={
                        <DialogFooter>
                            <Button onClick={signInAgain}>Entrar novamente</Button>
                        </DialogFooter>
                    }
                >
                    <img
                        src={sessionExpiredImg}
                        alt=""
                        // Intrinsic size, so the browser reserves the box instead
                        // of reflowing the modal when the art lands.
                        width={1134}
                        height={887}
                        decoding="async"
                        className="mx-auto h-40 w-auto object-contain sm:h-48"
                    />
                </AppDialogLayout>
            </DialogContent>
        </Dialog>
    );
};
